import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";

// "Today" is the America/New_York calendar day (the BD team works on ET),
// whatever the viewer's or server's clock says.
export const BD_TIMEZONE = "America/New_York";

// Today's ET calendar day as a UTC-midnight DateTime.
export function todayEt() {
  const t = DateTime.now().setZone(BD_TIMEZONE);
  return DateTime.utc(t.year, t.month, t.day);
}

export const currentEtYear = () => todayEt().year;

// Period presets. All maths is done in UTC calendar days to match the API.
// The dashboard is weekly by default: "This week" = Sunday -> today; "Last
// week" = the previous full Sunday -> Saturday week, which is what the BD
// team toggles back to for the Monday workplan review.
export const PRESETS = [
  { id: "this-week", label: "This week" },
  { id: "last-week", label: "Last week" },
  { id: "this-quarter", label: "This quarter" },
  { id: "last-quarter", label: "Last quarter" },
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "ytd", label: "Year to date" },
  { id: "this-year", label: "This year" },
  { id: "last-12m", label: "Last 12 months" },
  { id: "last-year", label: "Last year" },
  { id: "all-time", label: "All time" },
  { id: "custom", label: "Custom" },
];

// Which BD_WP_Point__c date places a point in the period (API `dateBasis`).
// "booked" (Booked On) matches the Salesforce "All BD WP Points" report and is
// the default; "accredited" (Accredited Date = call taken date for meetings /
// TAPs / LMVs) matches the AGP Workplan reports.
export const DATE_BASES = [
  { id: "booked", label: "Booked date", short: "Booked", hint: "Points count when they were booked (Booked On). Matches the Salesforce 'All BD WP Points' report." },
  { id: "accredited", label: "Accredited date", short: "Accredited", hint: "Points count on their Accredited Date (call taken date for meetings, TAPs and LMVs). Pending calls are excluded." },
];
export const DEFAULT_DATE_BASIS = "booked";

export const DEFAULT_PRESET = "this-week";

// Earliest synced BD data is a Closed Won deal from May 2016 - used for
// "All time" until meta.dataWindow is available.
const ALL_TIME_START = "2016-01-01";

// Sunday that starts the Sun-Sat week containing `day` (Luxon weekday: Mon=1..Sun=7).
export const startOfSunWeek = (day) => day.minus({ days: day.weekday % 7 });

// True when [from, to] (ISO dates) is exactly one Sunday -> Saturday week.
export function isSunSatWeek(from, to) {
  const a = DateTime.fromISO(from, { zone: "utc" });
  const b = DateTime.fromISO(to, { zone: "utc" });
  if (!a.isValid || !b.isValid) return false;
  return a.weekday === 7 && b.weekday === 6 && b.diff(a, "days").days === 6;
}

export function rangeForPreset(preset, now = todayEt(), dataWindow = null) {
  const today = now.startOf("day");
  switch (preset) {
    case "last-week": {
      const lastSaturday = today.minus({ days: (today.weekday % 7) + 1 });
      return { from: lastSaturday.minus({ days: 6 }), to: lastSaturday };
    }
    case "this-week":
      return { from: startOfSunWeek(today), to: today };
    case "this-year":
      // full calendar year so points booked later this year are included
      return { from: today.startOf("year"), to: today.endOf("year").startOf("day") };
    case "all-time": {
      const start = dataWindow && dataWindow.start ? DateTime.fromISO(dataWindow.start, { zone: "utc" }).startOf("day") : null;
      return { from: start && start.isValid ? start : DateTime.fromISO(ALL_TIME_START, { zone: "utc" }), to: today };
    }
    case "last-quarter": {
      const start = today.startOf("quarter").minus({ quarters: 1 });
      return { from: start, to: start.endOf("quarter").startOf("day") };
    }
    case "this-month":
      return { from: today.startOf("month"), to: today };
    case "last-month": {
      const start = today.startOf("month").minus({ months: 1 });
      return { from: start, to: start.endOf("month").startOf("day") };
    }
    case "ytd":
      return { from: today.startOf("year"), to: today };
    case "last-12m":
      return { from: today.minus({ months: 12 }).plus({ days: 1 }), to: today };
    case "last-year": {
      const start = today.startOf("year").minus({ years: 1 });
      return { from: start, to: start.endOf("year").startOf("day") };
    }
    case "this-quarter":
      return { from: today.startOf("quarter"), to: today };
    default:
      return rangeForPreset(DEFAULT_PRESET, now, dataWindow);
  }
}

const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

// `dataWindow` ({ start, end } ISO strings from GET /meta) bounds the
// "All time" preset; it is optional.
export default function usePeriod(dataWindow = null) {
  const [searchParams, setSearchParams] = useSearchParams();

  const preset = PRESETS.some((p) => p.id === searchParams.get("preset"))
    ? searchParams.get("preset")
    : searchParams.get("from") && searchParams.get("to")
    ? "custom"
    : DEFAULT_PRESET;
  const compare = searchParams.get("compare") === "1";
  const dateBasis = DATE_BASES.some((d) => d.id === searchParams.get("dateBasis"))
    ? searchParams.get("dateBasis")
    : DEFAULT_DATE_BASIS;

  const windowStart = dataWindow && dataWindow.start ? String(dataWindow.start) : "";
  const range = useMemo(() => {
    if (preset === "custom" && isIso(searchParams.get("from")) && isIso(searchParams.get("to"))) {
      return { from: searchParams.get("from"), to: searchParams.get("to") };
    }
    const r = rangeForPreset(preset, todayEt(), windowStart ? { start: windowStart } : null);
    return { from: r.from.toISODate(), to: r.to.toISODate() };
  }, [preset, searchParams, windowStart]);

  const update = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "" || v === false) next.delete(k);
        else next.set(k, String(v === true ? 1 : v));
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setPreset = useCallback(
    (id) => {
      if (id === "custom") {
        update({ preset: "custom", from: range.from, to: range.to });
      } else {
        update({ preset: id, from: null, to: null });
      }
    },
    [update, range]
  );

  const setCustomRange = useCallback(
    (from, to) => {
      if (!isIso(from) || !isIso(to)) return;
      const [a, b] = from <= to ? [from, to] : [to, from];
      update({ preset: "custom", from: a, to: b });
    },
    [update]
  );

  const setCompare = useCallback((on) => update({ compare: on ? 1 : null }), [update]);

  // Step the current Sun-Sat week backwards / forwards. Lands on the
  // "last-week" / "this-week" presets when the target week matches them,
  // otherwise a custom 7-day range. Never steps past the current week.
  const isWeek = isSunSatWeek(range.from, range.to) || preset === "this-week";
  const shiftWeek = useCallback(
    (delta) => {
      const today = todayEt();
      const currentStart = startOfSunWeek(DateTime.fromISO(range.from, { zone: "utc" }));
      const start = currentStart.plus({ weeks: delta });
      if (start > today) return;
      const lastWeek = rangeForPreset("last-week", today);
      const thisWeek = rangeForPreset("this-week", today);
      if (start.equals(lastWeek.from)) return update({ preset: "last-week", from: null, to: null });
      if (start.equals(thisWeek.from)) return update({ preset: "this-week", from: null, to: null });
      return update({ preset: "custom", from: start.toISODate(), to: start.plus({ days: 6 }).toISODate() });
    },
    [range.from, update]
  );
  const canShiftWeekForward = isWeek && startOfSunWeek(DateTime.fromISO(range.from, { zone: "utc" })).plus({ weeks: 1 }) <= todayEt();

  const setDateBasis = useCallback(
    (id) => update({ dateBasis: DATE_BASES.some((d) => d.id === id) && id !== DEFAULT_DATE_BASIS ? id : null }),
    [update]
  );

  // query params passed straight through to the API
  const apiParams = useMemo(
    () => ({
      from: range.from,
      to: range.to,
      ...(compare ? { compare: 1 } : {}),
      ...(dateBasis !== DEFAULT_DATE_BASIS ? { dateBasis } : {}),
    }),
    [range, compare, dateBasis]
  );

  // preserves period state when navigating between pages
  const search = useMemo(() => {
    const p = new URLSearchParams();
    if (preset !== DEFAULT_PRESET) p.set("preset", preset);
    if (preset === "custom") {
      p.set("from", range.from);
      p.set("to", range.to);
    }
    if (compare) p.set("compare", "1");
    if (dateBasis !== DEFAULT_DATE_BASIS) p.set("dateBasis", dateBasis);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [preset, range, compare, dateBasis]);

  const dateBasisDef = DATE_BASES.find((d) => d.id === dateBasis) || DATE_BASES[0];

  return {
    preset,
    range,
    compare,
    dateBasis,
    dateBasisDef,
    // point field on API rows that corresponds to the selected basis (both are
    // UTC-midnight calendar days; bookedOnDay is the ET day of Booked On)
    pointDateField: dateBasis === "accredited" ? "accreditedDate" : "bookedOnDay",
    apiParams,
    search,
    isWeek,
    canShiftWeekForward,
    shiftWeek,
    setPreset,
    setCustomRange,
    setCompare,
    setDateBasis,
  };
}
